import { useImperativeHandle, useMemo, useRef, useState, type Ref } from 'react'
import { useDraggable, useDroppable, useDndMonitor } from '@dnd-kit/core'
import type { TimelineItem } from '@/types/timeline'
import type { FamilyMember, FamilyMemberColor } from '@/types/family'
import { FAMILY_COLORS } from '@/types/family'
import { buildDayScale, timeAtOffset, MIN_BLOCK, type DayScale, type ScaleBlock } from '@/lib/today/dayScale'
import { SCALE_DROP_ID, refusalFor } from '@/lib/today/todayDrop'
import { dragPointerY } from '@/lib/today/dragPointer'
import { isPlanDrag, readPlanDrag, type PlanDragPayload } from '@/lib/planning/planDrag'
import { useTravelTime } from '@/hooks/useTravelTime'
import { effectiveStartTime, formatTimeLong } from '@/lib/timeUtils'
import type { ProposedRow, RowMark } from './todayPreview'
import { TaskCheckbox } from './TaskCheckbox'
import { TimelineQuickInput, type TimelineCaptureResult } from './TimelineQuickInput'
import type { ParserContext } from '@/lib/quickInputParser'

/**
 * The day, to scale (Scott, 2026-10-06, option B): Today's timed day as a
 * column of hours in the chosen place's colours: each thing where it falls,
 * free stretches named, a now line. It IS the schedule — the timed list it
 * replaced is gone, so nothing draws the day twice.
 *
 * A task or routine is ticked off by its own check (Scott, 2026-10-17); a
 * click elsewhere opens the thing's details (fine-tune its time there).
 * Everything moves by dragging; a drop lands on the quarter hour under the
 * pointer, and the list beside it ("For today") and the week column drop
 * onto it to give something a time.
 *
 * An empty stretch takes a click (Scott, 2026-11-03: "we need to be able to
 * enter items directly on to the calendar grid"): a box opens at that quarter
 * hour, Task or Event, typed the way Add task is typed; Enter puts it there.
 */

export interface DayScaleHandle {
  /** The time at a viewport y (where something was let go), on the viewed day. */
  timeAt: (viewportTop: number) => Date | null
}

const DEFAULT_MINUTES = 30

/** "9:00", "4:15" — the column's own a/p labels say which half of the day. */
function clock(d: Date): string {
  const h = d.getHours() % 12 || 12
  return `${h}:${String(d.getMinutes()).padStart(2, '0')}`
}

function driveMinutes(label: string): number {
  const h = /(\d+)\s*hr/.exec(label)
  const m = /(\d+)\s*min/.exec(label)
  return (h ? Number(h[1]) * 60 : 0) + (m ? Number(m[1]) : 0)
}

function MiniAvatar({ member }: { member: FamilyMember }) {
  const c = FAMILY_COLORS[member.color as FamilyMemberColor] ?? FAMILY_COLORS.blue
  return (
    <span className={`today-scale-avatar ${c.bg} ${c.text}`} title={member.name} aria-hidden="true">
      {member.initials || member.name.slice(0, 1)}
    </span>
  )
}

interface BlockProps {
  item: TimelineItem
  start: Date
  end: Date
  /** It has an end of its own (not the half-hour a bare time is given). */
  hasEnd: boolean
  block: ScaleBlock
  pxPerHour: number
  people: FamilyMember[]
  selected: boolean
  upNext: boolean
  past: boolean
  refused: boolean
  /** A pending change card's mark on this thing (todayPreview.ts). */
  mark?: RowMark
  onSelect: (id: string) => void
  /** Ticks it off (or back on). Unset: no check — events have none. */
  onToggleDone?: () => void
  /** A new end, from dragging the bottom edge. Unset: it cannot be resized
   *  (a routine has no length of its own; a read-only event cannot change). */
  onResize?: (end: Date) => void
}

/** Lengths snap to the quarter hour, as drops do, and never go below one. */
const SNAP_MINUTES = 15

/** Every thing on the day is drawn one way (Scott, 2026-10-07: three styles
 *  side by side "looks stupid"): "9:00–10:15 · Boxing" and who carries it.
 *  A task or routine leads with the same check "For today" has (Scott,
 *  2026-10-17); an event has none. A click on the rest opens its details. */
function ScaleItem({ item, start, end: savedEnd, hasEnd: savedHasEnd, block, pxPerHour, people, selected, upNext, past, refused, mark, onSelect, onToggleDone, onResize }: BlockProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: item.id, disabled: refused })
  // Dragging the bottom edge (Scott, 2026-10-30): the block follows the
  // pointer in quarter hours and the new end is written on release.
  const [resize, setResize] = useState<{ y: number; minutes: number; to: number } | null>(null)
  const resizeTo = resize?.to ?? null
  const lengthAt = (from: { y: number; minutes: number }, clientY: number) => {
    const raw = from.minutes + ((clientY - from.y) / pxPerHour) * 60
    return Math.max(SNAP_MINUTES, Math.round(raw / SNAP_MINUTES) * SNAP_MINUTES)
  }
  const resizeHandle = onResize && !refused ? {
    onPointerDown: (e: React.PointerEvent) => {
      e.stopPropagation()
      e.preventDefault()
      e.currentTarget.setPointerCapture?.(e.pointerId)
      const minutes = Math.round((savedEnd.getTime() - start.getTime()) / 60_000)
      setResize({ y: e.clientY, minutes, to: minutes })
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (resize) setResize({ ...resize, to: lengthAt(resize, e.clientY) })
    },
    onPointerUp: (e: React.PointerEvent) => {
      if (!resize) return
      const minutes = lengthAt(resize, e.clientY)
      setResize(null)
      if (minutes !== resize.minutes || !savedHasEnd) onResize(new Date(start.getTime() + minutes * 60_000))
    },
    onPointerCancel: () => setResize(null),
    // The press is not picking the block up (the drag sensors listen for
    // these on the wrapper).
    onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
    onTouchStart: (e: React.TouchEvent) => e.stopPropagation(),
  } : null
  const end = resizeTo !== null ? new Date(start.getTime() + resizeTo * 60_000) : savedEnd
  const hasEnd = resizeTo !== null || savedHasEnd
  const isEvent = item.type === 'event'
  // How far away it is, drawn above it as the time it takes to get there.
  const travel = useTravelTime(item, { enabled: isEvent && !item.completed && !past })
  const driveH = travel ? Math.max(14, (driveMinutes(travel) / 60) * pxPerHour) : 0
  const time = hasEnd ? `${clock(start)}–${clock(end)}` : clock(start)
  const lanes = block.lanes
  const pos = {
    top: block.top,
    height: resizeTo !== null ? Math.max(MIN_BLOCK, (resizeTo / 60) * pxPerHour) : block.height,
    left: `calc(var(--scale-gutter) + (100% - var(--scale-gutter)) * ${block.lane / lanes})`,
    width: `calc((100% - var(--scale-gutter)) / ${lanes} - ${lanes > 1 ? 6 : 0}px)`,
  }
  const done = item.completed || !!item.skipped
  const label = `${item.title}, ${formatTimeLong(start)}${hasEnd ? ` to ${formatTimeLong(end)}` : ''}${done ? ', done' : ''}${upNext ? ', up next' : ''}`
  return (
    <>
      {travel && block.lane === 0 && block.top - driveH > 0 && (
        <div className="today-scale-drive" style={{ top: block.top - driveH, height: driveH, left: pos.left }} aria-hidden="true">
          {travel}
        </div>
      )}
      <div
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        // The drag handle is the whole block; the button inside is the
        // keyboard's way in, so the wrapper is not a tab stop of its own.
        tabIndex={-1}
        role={undefined}
        data-item-id={item.id}
        className={[
          'today-scale-item',
          done ? 'is-done' : '',
          past && !upNext ? 'is-past' : '',
          selected ? 'is-selected' : '',
          isDragging ? 'is-dragging' : '',
          resizeTo !== null ? 'is-resizing' : '',
          mark ? `is-preview is-${mark.tone}` : '',
          mark?.struck ? 'is-struck' : '',
        ].filter(Boolean).join(' ')}
        style={pos}
      >
        {onToggleDone && (
          // Pressing the check is not picking the block up: the press stays
          // out of the drag listeners on the wrapper.
          <span className="today-scale-check" onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
            <TaskCheckbox
              shape="circle"
              completed={item.completed}
              onToggleComplete={onToggleDone}
              onToggleWaiting={() => {}}
              label={item.completed ? `Mark ${item.title} incomplete` : `Mark ${item.title} complete`}
            />
          </span>
        )}
        <button type="button" className="today-scale-open" aria-label={label} onClick={() => onSelect(item.id)}>
          <span className="today-scale-line"><span className="tabular-nums">{time}</span> · <span>{item.title}</span></span>
          {mark?.note && <span className="wc-proposed-note">{mark.note}</span>}
        </button>
        {people.length > 0 && (
          <span className="today-scale-people">{people.slice(0, 3).map((m) => <MiniAvatar key={m.id} member={m} />)}</span>
        )}
        {resizeHandle && (
          <span className="today-scale-resize" aria-hidden="true" data-testid="scale-resize" {...resizeHandle} />
        )}
      </div>
    </>
  )
}

/** A thing a pending change card would put here: dashed teal, inert — the
 *  card is where it is approved, not the column. */
function ProposedBlock({ row, block }: { row: ProposedRow; block: ScaleBlock }) {
  const lanes = block.lanes
  return (
    <div
      className="today-scale-item is-preview is-proposed"
      data-testid="proposed-row"
      style={{
        top: block.top,
        height: block.height,
        left: `calc(var(--scale-gutter) + (100% - var(--scale-gutter)) * ${block.lane / lanes})`,
        width: `calc((100% - var(--scale-gutter)) / ${lanes} - ${lanes > 1 ? 6 : 0}px)`,
      }}
    >
      <span className="today-scale-open">
        <span className="today-scale-line"><span className="tabular-nums">{clock(row.time!)}</span> · <span>{row.title}</span></span>
        {row.note && <span className="wc-proposed-note">{row.note}</span>}
        <span className="sr-only"> (proposed, not saved yet)</span>
      </span>
    </div>
  )
}

export function TodayDayScale({
  items,
  viewedDate,
  now,
  isMobile,
  selectedItemId,
  upNextId,
  peopleOf,
  isReadOnlyEvent,
  onSelect,
  onToggleDone,
  onPlanDrop,
  fromHour = null,
  handleRef,
  markOf,
  proposed = [],
  showOpenTime = true,
  onResize,
  create,
}: {
  /** The day's timed rows (a group's children ride with their parent). */
  items: TimelineItem[]
  viewedDate: Date
  /** The clock on today; null on any other day (no now line, no past). */
  now: Date | null
  isMobile: boolean
  selectedItemId: string | null
  upNextId?: string
  peopleOf: (item: TimelineItem) => FamilyMember[]
  isReadOnlyEvent: (item: TimelineItem) => boolean
  /** Opens the thing's details pane, as a row's click does. */
  onSelect: (id: string) => void
  /** Ticks a task or routine off (or back on), as its check in the list does. */
  onToggleDone?: (item: TimelineItem) => void
  /** A row dragged in from the week column (native drag), with the time it
   *  was dropped at. */
  onPlanDrop?: (payload: PlanDragPayload, when: Date) => void
  /** "Hide earlier hours": the column opens at this hour. */
  fromHour?: number | null
  handleRef?: Ref<DayScaleHandle>
  /** A pending change card's mark on a thing already on the day. */
  markOf?: (item: TimelineItem) => RowMark | undefined
  /** A pending change card's timed arrivals: dashed, inert, at their time. */
  proposed?: readonly ProposedRow[]
  /** Missing or incomplete calendars cannot establish available time. */
  showOpenTime?: boolean
  /** A new end for a task or event, from dragging its bottom edge. */
  onResize?: (item: TimelineItem, end: Date) => void
  /** Typing straight onto an empty stretch. Unset: the column takes no clicks
   *  of its own. Without onCreateEvent it adds tasks only. */
  create?: {
    parserContext: ParserContext
    onCreateTask: (r: TimelineCaptureResult) => void
    onCreateEvent?: (r: TimelineCaptureResult) => void
  }
}) {
  // When each thing runs. An all-day "Dinner: …" event sits at its meal's
  // hour (effectiveStartTime), for the half hour a bare time gets.
  const timed = useMemo(() => items.flatMap((item) => {
    const start = item.isSubtask ? null : effectiveStartTime(item)
    if (!start) return []
    const own = item.allDay ? null : item.endTime
    const hasEnd = !!own && own.getTime() > start.getTime()
    return [{ item, start, end: hasEnd ? own! : new Date(start.getTime() + DEFAULT_MINUTES * 60_000), hasEnd }]
  }), [items])
  const proposedById = useMemo(
    () => new Map(proposed.filter((p) => p.time).map((p) => [`proposed-${p.key}`, p])),
    [proposed],
  )
  const scale: DayScale = useMemo(
    () => buildDayScale(
      [
        ...timed.map((t) => ({ id: t.item.id, start: t.start, end: t.hasEnd ? t.end : null })),
        ...[...proposedById].map(([id, p]) => ({ id, start: p.time!, end: null })),
      ],
      { now, pxPerHour: isMobile ? 40 : 48, fromHour },
    ),
    [timed, proposedById, now, isMobile, fromHour],
  )
  const trackRef = useRef<HTMLDivElement | null>(null)
  const { setNodeRef, isOver } = useDroppable({ id: SCALE_DROP_ID })
  const setTrack = (el: HTMLDivElement | null) => { trackRef.current = el; setNodeRef(el) }

  const timeAt = (viewportTop: number) => {
    const rect = trackRef.current?.getBoundingClientRect()
    return rect ? timeAtOffset(scale, viewportTop - rect.top, viewedDate) : null
  }
  useImperativeHandle(handleRef, () => ({ timeAt }))

  // While something is dragged over the column, a line says where it lands.
  const [landing, setLanding] = useState<{ top: number; label: string } | null>(null)
  useDndMonitor({
    onDragMove(e) {
      const y = dragPointerY(e)
      const rect = trackRef.current?.getBoundingClientRect()
      if (e.over?.id !== SCALE_DROP_ID || y == null || !rect) { setLanding(null); return }
      const when = timeAtOffset(scale, y - rect.top, viewedDate)
      const h = when.getHours() + when.getMinutes() / 60
      setLanding({ top: (h - scale.startHour) * scale.pxPerHour, label: formatTimeLong(when) })
    },
    onDragEnd() { setLanding(null) },
    onDragCancel() { setLanding(null) },
  })

  // The week column's rows travel by native drag (they live outside this
  // page's dnd-kit context): the same landing line, read from the pointer.
  const landAt = (clientY: number) => {
    const when = timeAt(clientY)
    if (!when) return null
    const h = when.getHours() + when.getMinutes() / 60
    setLanding({ top: (h - scale.startHour) * scale.pxPerHour, label: formatTimeLong(when) })
    return when
  }
  const planDrop = onPlanDrop ? {
    onDragOver: (e: React.DragEvent) => {
      if (!isPlanDrag(e.dataTransfer)) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      landAt(e.clientY)
    },
    onDragLeave: (e: React.DragEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setLanding(null)
    },
    onDrop: (e: React.DragEvent) => {
      const payload = readPlanDrag(e.dataTransfer)
      setLanding(null)
      if (!payload) return
      e.preventDefault()
      e.stopPropagation()
      const when = timeAt(e.clientY)
      if (when) onPlanDrop(payload, when)
    },
  } : {}

  const byId = useMemo(() => new Map(timed.map((t) => [t.item.id, t])), [timed])

  // ── Typing onto the grid ──
  // A click lands on the quarter hour it falls in (4:00–4:14 is 4:00), not
  // the nearest one: the slot you clicked is the slot you get.
  const [draft, setDraft] = useState<{ top: number; when: Date; kind: 'task' | 'event' } | null>(null)
  const [hover, setHover] = useState<{ top: number; label: string } | null>(null)
  const slotAt = (clientY: number) => {
    const rect = trackRef.current?.getBoundingClientRect()
    if (!rect) return null
    const when = timeAtOffset(scale, clientY - rect.top - scale.pxPerHour / 8, viewedDate)
    const h = when.getHours() + when.getMinutes() / 60
    return { when, top: (h - scale.startHour) * scale.pxPerHour }
  }
  // Only the column itself, its hour lines and its free stretches open a box.
  const onEmpty = (target: EventTarget) =>
    target instanceof Element && !target.closest('.today-scale-item, .today-scale-draft')
  const createHandlers = create ? {
    onClick: (e: React.MouseEvent) => {
      if (!onEmpty(e.target) || draft) return
      const at = slotAt(e.clientY)
      if (at) { setHover(null); setDraft({ ...at, kind: 'task' }) }
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (e.pointerType !== 'mouse' || draft || landing || !onEmpty(e.target)) { if (hover) setHover(null); return }
      const at = slotAt(e.clientY)
      if (at && at.top !== hover?.top) setHover({ top: at.top, label: formatTimeLong(at.when) })
    },
    onPointerLeave: () => setHover(null),
  } : {}
  const submitDraft = (r: TimelineCaptureResult) => {
    if (!draft || !create) return
    if (draft.kind === 'event' && create.onCreateEvent) create.onCreateEvent(r)
    else create.onCreateTask(r)
    setDraft(null)
  }

  const nowMs = now?.getTime() ?? null
  const nowLabel = now ? `now ${clock(now)}` : null

  return (
    <div
      ref={setTrack}
      className={`today-scale${isOver ? ' is-over' : ''}${create ? ' is-writable' : ''}`}
      style={{ height: scale.height }}
      data-testid="today-day-scale"
      {...planDrop}
      {...createHandlers}
    >
      {scale.ticks.map((t) => (
        <div key={t.label} className="today-scale-hour" style={{ top: t.top }} aria-hidden="true"><span>{t.label}</span></div>
      ))}
      {showOpenTime && scale.free.map((f) => (
        <div key={f.top} className="today-scale-free" style={{ top: f.top, height: f.height }}>{f.label}</div>
      ))}
      {scale.blocks.map((b) => {
        const arriving = proposedById.get(b.id)
        if (arriving) return <ProposedBlock key={b.id} row={arriving} block={b} />
        const entry = byId.get(b.id)
        if (!entry) return null
        const { item, start, end, hasEnd } = entry
        const past = nowMs !== null && end.getTime() <= nowMs
        return (
          <ScaleItem
            key={b.id}
            item={item}
            start={start}
            end={end}
            hasEnd={hasEnd}
            block={b}
            pxPerHour={scale.pxPerHour}
            people={peopleOf(item)}
            selected={selectedItemId === item.id}
            upNext={upNextId === item.id}
            past={past}
            refused={!!refusalFor(item, isReadOnlyEvent)}
            mark={markOf?.(item)}
            onSelect={onSelect}
            onToggleDone={onToggleDone && (item.type === 'task' || item.type === 'routine') ? () => onToggleDone(item) : undefined}
            onResize={onResize && (item.type === 'task' || item.type === 'event') && !item.isSubtask && !markOf?.(item) ? (to) => onResize(item, to) : undefined}
          />
        )
      })}
      {scale.now !== null && (
        <div className="today-scale-now" style={{ top: scale.now }} aria-hidden="true"><span>{nowLabel}</span></div>
      )}
      {landing && (
        <div className="today-scale-landing" style={{ top: landing.top }} aria-hidden="true"><span>{landing.label}</span></div>
      )}
      {hover && !landing && (
        <div className="today-scale-hint" style={{ top: hover.top }} aria-hidden="true"><span>+ {hover.label}</span></div>
      )}
      {draft && create && (
        <div className="today-scale-draft" style={{ top: draft.top }} role="group" aria-label={`Add at ${formatTimeLong(draft.when)}`}>
          <div className="today-scale-draft-head">
            <span className="tabular-nums">{formatTimeLong(draft.when)}</span>
            {create.onCreateEvent && (
              <span className="today-scale-draft-kinds" role="radiogroup" aria-label="Kind">
                {(['task', 'event'] as const).map((k) => (
                  <button key={k} type="button" role="radio" aria-checked={draft.kind === k}
                    // Keeps the input focused (its blur closes the box).
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setDraft({ ...draft, kind: k })}>
                    {k === 'task' ? 'Task' : 'Event'}
                  </button>
                ))}
              </span>
            )}
          </div>
          <TimelineQuickInput
            kind={draft.kind}
            anchorTime={draft.when}
            parserContext={create.parserContext}
            onSubmit={submitDraft}
            onCancel={() => setDraft(null)}
            inputClassName="today-scale-draft-input"
          />
        </div>
      )}
    </div>
  )
}
